import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import isArchived from 'archive/isArchived';
import * as BenchlingUtil from 'BenchlingUtil';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import * as ProcessSchemasDAO from 'processes/dao/ProcessSchemaDAO';
import isSchemaExecutionTemplateUnpublished from 'processes/settings/isSchemaExecutionTemplateUnpublished';
import WarningBadge from 'ui-library/WarningBadge';

import {GetTaskSchemaConfigurationIssuesQuery} from './__generated/GetTaskSchemaConfigurationIssuesQuery';
import EmptySidebarIcon from './EmptySidebarIcon';
import {Section, SettingsSection} from './SidebarConstants';
import SidebarItem from './SidebarItem';

interface ProcessSchemaSettingsProps {
  section: SettingsSection;
}

export const GET_TASK_SCHEMA_CONFIGURATION_ISSUES_QUERY = gql`
  query GetTaskSchemaConfigurationIssuesQuery @skipAudit @engComponent(name: WORKFLOWS) {
    processTaskSchemasSupportingPlatformFeature(platformFeature: SCHEMA_CONFIG) {
      id
      name
      archiveRecord {
        purpose
      }
      entryTemplate {
        id
        currentPublishingRecord {
          ... on PublishingRecordNode {
            id
            publishingStatus
          }
          ...InaccessibleInfoGraphQL
        }
      }
      usesArchivedTaskSchema
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function ProcessSchemaSettings(props: ProcessSchemaSettingsProps): JSX.Element {
  const response = useBenchlingQuery<GetTaskSchemaConfigurationIssuesQuery>(
    GET_TASK_SCHEMA_CONFIGURATION_ISSUES_QUERY
  );
  // We don't show any kind of loading state here because the badge only appears in edge cases and adding an
  // explicit loading state may add additional confusion. This also matches other setting sidebar items like
  // registry settings which shows a similar badge.
  const unpublishedSchemasCount =
    response.type === QueryResultType.SUCCESS
      ? response.data.processTaskSchemasSupportingPlatformFeature.filter(
          (schema) => !isArchived(schema) && isSchemaExecutionTemplateUnpublished(schema)
        ).length
      : 0;

  const usesArchivedTaskSchemaCount =
    response.type === QueryResultType.SUCCESS
      ? response.data.processTaskSchemasSupportingPlatformFeature.filter(
          (schema) => !isArchived(schema) && schema.usesArchivedTaskSchema
        ).length
      : 0;

  const numSchemasWithConfigurationIssues = unpublishedSchemasCount + usesArchivedTaskSchemaCount;

  return (
    <div>
      <div className="browser-nav-header">Workflow Schemas</div>
      <SidebarItem
        active={props.section === Section.PROCESS_SCHEMAS}
        icon={<EmptySidebarIcon />}
        name="Task schemas"
        badge={
          numSchemasWithConfigurationIssues > 0 ? (
            <WarningBadge
              content={numSchemasWithConfigurationIssues}
              tooltipTitle={`${numSchemasWithConfigurationIssues} ${BenchlingUtil.pluralize(numSchemasWithConfigurationIssues, 'task schema has', 'task schemas have')} configuration issues`}
              className={css(styles.sidebarItemWarningBadge)}
              dataTestAttrs={{component: 'ProcessSchemaSettings', element: 'configurationIssuesBadge'}}
            />
          ) : null
        }
        link={ProcessSchemasDAO.processTaskSchemasURL()}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  sidebarItemWarningBadge: {
    margin: '-3px 0',
  },
  badgeText: {
    marginLeft: 8,
  },
});
